import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import ModulePopup from "./Modalpopup";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../services/AxiosInstance';
import { getActiveSubscriptions, clearSubscriptionCache } from '../../utils/subscriptionCache';
import { sortSubscriptionPlansBySortOrder } from '../../utils/subscriptionPlansSort';
import {
  getModuleTabsFromPlans,
  getModuleTabIcon,
  getModuleDescriptionForTab,
  planMatchesModuleTab,
  subscriptionMatchesModuleTab,
  resolveInitialModuleTab,
  subscriptionPlansApiUrl,
} from '../../utils/subscriptionModuleTabs';
import { planIncludesCredits } from '../../services/CreditService';

const CorporateSubscription = ({
  formData,
  onSubmit,
  activePlanId,
  parentSelectedPlan,
  onPlanSelect,
  onBillingCycleChange,
  onModuleTabChange,
  initialModuleTab,
  /** Registration flow: one plan per module, selections kept across tabs */
  allowMultiModuleCart = false,
  suppressModulePopupOnSelect = false,
  selectedPlansByModuleId = null,
}) => {
  const navigate = useNavigate();

  // Static promo code data (keeping this as is since API wasn't provided for promo codes)
  const [promoCodeList] = useState([
    { PromoCodeName: "WELCOME20", Discount: 20, ShortDescription: "20% off for new users" },
    { PromoCodeName: "SUMMER30", Discount: 30, ShortDescription: "30% off summer sale" },
    { PromoCodeName: "YEARLY50", Discount: 50, ShortDescription: "50% off yearly plans" }
  ]);

  // API data state
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
  const [modulesList, setModulesList] = useState([]);

  // Fetch modules list
  useEffect(() => {
    const fetchModules = async () => {
      try {
        const response = await axiosInstance.get('/modules/list');
        // Handle different response structures
        const modulesData = response.data || response;
        // Ensure it's an array
        if (Array.isArray(modulesData)) {
          setModulesList(modulesData);
        } else if (modulesData && Array.isArray(modulesData.data)) {
          setModulesList(modulesData.data);
        } else {
          setModulesList([]);
        }
      } catch (error) {
        console.error('Error fetching modules:', error);
        setModulesList([]);
      }
    };

    fetchModules();
  }, []);

  // Fetch subscription plans from API
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(subscriptionPlansApiUrl());

        if (response.data.success) {
          // Transform API data to match component structure
          const transformedPlans = sortSubscriptionPlansBySortOrder(
            response.data.data.map(plan => ({
            id: plan.id,
            module_id: plan.module_id,
            PlanName: plan.display_name,
            monthly_price: plan.monthly_price,
            yearly_price: plan.yearly_price,
            PlanDuration: `${plan.duration_days} Days`,
            duration: plan.duration_days,
            Modules: plan.PlanModules?.map(module => module.id) || [],
            features: plan.features_list || [],
            whats_included: plan.whats_included || [],
            isTrialAllowed: plan.trial_allowed,
            trialDays: plan.is_trial ? 7 : 0, // Default trial days
            isActive: plan.is_active,
            PlanModules: plan.PlanModules || [],
            max_users: plan.max_users,
            billing_type: plan.billing_type,
            Desscription: plan.description,
            is_unlimited_users: plan.is_unlimited_users,
            Module: plan.Module || null, // Preserve Module object for filtering
            monthly_included_credits: plan.monthly_included_credits,
            yearly_included_credits: plan.yearly_included_credits,
            sort_order: plan.sort_order ?? 0,
          }))
          );

          setSubscriptionPlans(transformedPlans);
        } else {
          setError('Failed to fetch subscription plans');
          toast.error('Failed to fetch subscription plans');
        }
      } catch (error) {
        console.error('Error fetching subscription plans:', error);
        setError('Error fetching subscription plans');
        toast.error('Error fetching subscription plans');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionPlans();
  }, []);

  // Fetch active subscriptions
  useEffect(() => {
    const fetchActiveSubscriptions = async () => {
      try {
        setSubscriptionsLoading(true);
        // Use shared cache utility
        const response = await getActiveSubscriptions();
        
        if (response && response.subscriptions) {
          setActiveSubscriptions(response.subscriptions);
        }
      } catch (error) {
        console.error('Error fetching active subscriptions:', error);
      } finally {
        setSubscriptionsLoading(false);
      }
    };

    fetchActiveSubscriptions();

    // Listen for subscription updates
    const handleSubscriptionUpdate = () => {
      // Clear cache and fetch fresh data
      clearSubscriptionCache();
      fetchActiveSubscriptions();
    };

    window.addEventListener('user-subscription-updated', handleSubscriptionUpdate);
    
    return () => {
      window.removeEventListener('user-subscription-updated', handleSubscriptionUpdate);
    };
  }, []);


  const [promoCode, setPromoCode] = useState('');
  const [validPromo, setValidPromo] = useState(null);
  const [promoApplied, setPromoApplied] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const handleBillingCycleChange = (cycle) => {
    setBillingCycle(cycle);
    onBillingCycleChange?.(cycle);
  };

  useEffect(() => {
    onBillingCycleChange?.(billingCycle);
  }, []);
  const [showModules, setShowModules] = useState(false);
  const [selectedModules, setSelectedModules] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [activeModuleTab, setActiveModuleTab] = useState(() =>
    resolveInitialModuleTab(initialModuleTab, [], 'task')
  );

  const moduleTabs = getModuleTabsFromPlans(subscriptionPlans);

  // Sync active tab when initialModuleTab or plans load
  useEffect(() => {
    if (subscriptionPlans.length === 0) return;
    const resolved = resolveInitialModuleTab(initialModuleTab, subscriptionPlans, moduleTabs[0]?.key || 'task');
    setActiveModuleTab(resolved);
  }, [initialModuleTab, subscriptionPlans.length]);

  // Parent cleared selection (e.g. module tab switch) — clear local highlight state
  useEffect(() => {
    if (allowMultiModuleCart) return;
    if (!parentSelectedPlan) {
      setSelectedPlanId(null);
      setSelectedPlan(null);
    }
  }, [parentSelectedPlan, allowMultiModuleCart]);

  const handleModuleTabChange = (tab) => {
    if (tab === activeModuleTab) return;
    setActiveModuleTab(tab);
    onModuleTabChange?.(tab);
  };

  // Helper function to format bytes
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    if (bytes < 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleApplyPromo = () => {
    const found = promoCodeList.find(p => p.PromoCodeName.toLowerCase() === promoCode.toLowerCase());
    if (found) {
      setValidPromo(found);
      setPromoApplied(true);
      toast.success('Promo code applied successfully!');
    } else {
      setValidPromo(null);
      setPromoApplied(false);
      toast.error('Invalid promo code');
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlanId(plan.id);
    setSelectedPlan(plan);

    // Notify parent component about the selected plan
    if (onPlanSelect) {
      onPlanSelect(plan);
    }

    // Get modules from the selected plan's PlanModules
    const planModules = plan.PlanModules || [];
    const matchedModules = planModules.map(module => ({
      ModuleId: module.id,
      ModuleName: module.module_name,
      Description: `${module.module_name} - ${module.user_limit} users limit`,
      UserLimit: module.user_limit,
      IsEnabled: module.is_enabled,
      PlanModuleFeatures: module.PlanModuleFeatures || []
    }));

    setSelectedModules(matchedModules || []);
    if (!suppressModulePopupOnSelect) {
      setShowModules(true);
    } else {
      setShowModules(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    if (!selectedModules || selectedModules.length === 0) {
      toast.error('Please select a subscription plan that includes at least one module.');
      setSubmitLoading(false);
      return;
    }

    // Simulate API call
    setTimeout(() => {
      toast.success('Payment submitted successfully!');
      setTimeout(() => {
        window.location.reload();
      }, 2000);
      setSubmitLoading(false);
    }, 2000);
  };

  const calculateDiscountedPrice = (plan) => {
    const price = billingCycle === 'monthly' ? plan.monthly_price : plan.yearly_price;
    if (promoApplied && validPromo) {
      const discountAmount = (price * parseInt(validPromo.Discount)) / 100;
      return price - discountAmount;
    }
    return parseFloat(price);
  };

  const getPlansByModule = (moduleType) => {
    return subscriptionPlans.filter((plan) => planMatchesModuleTab(plan, moduleType));
  };

  const filteredPlans = getPlansByModule(activeModuleTab);

  // Multi-module cart: highlight the plan chosen for the active tab (from parent map)
  useEffect(() => {
    if (!allowMultiModuleCart || !selectedPlansByModuleId || typeof selectedPlansByModuleId !== 'object') {
      return;
    }
    const fp = getPlansByModule(activeModuleTab);
    const moduleId = fp[0]?.module_id;
    if (moduleId == null) return;
    const picked = selectedPlansByModuleId[String(moduleId)] || selectedPlansByModuleId[moduleId];
    if (picked && picked.id != null) {
      setSelectedPlanId(picked.id);
      setSelectedPlan(picked);
    } else {
      setSelectedPlanId(null);
      setSelectedPlan(null);
    }
  }, [allowMultiModuleCart, activeModuleTab, selectedPlansByModuleId, subscriptionPlans]);

  const getSubscriptionsByModule = (moduleType) => {
    if (!activeSubscriptions || activeSubscriptions.length === 0) return [];
    return activeSubscriptions.filter((subscription) =>
      subscriptionMatchesModuleTab(subscription, moduleType)
    );
  };

  const filteredSubscriptions = getSubscriptionsByModule(activeModuleTab);

  // Get current subscription for the active module (for renewal period logic)
  const currentModuleSubscription = filteredSubscriptions && filteredSubscriptions.length > 0 ? filteredSubscriptions[0] : null;

  // Renewal notification period: 5 days for monthly, 10 days for yearly (per doc Section 7.1)
  const isWithinRenewalPeriod = (subscription) => {
    if (!subscription?.end_date) return false;
    const endDate = new Date(subscription.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    const daysUntilExpiry = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    const subType = (subscription.subscription_type || '').toLowerCase();
    const renewalDays = subType === 'yearly' ? 10 : 5; // monthly, trial, custom use 5 days
    return daysUntilExpiry >= 0 && daysUntilExpiry <= renewalDays;
  };

  const isSubscriptionExpired = (subscription) => {
    if (!subscription?.end_date) return false;
    const endDate = new Date(subscription.end_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  };

  const withinRenewalPeriod = currentModuleSubscription ? isWithinRenewalPeriod(currentModuleSubscription) : false;
  const subscriptionExpired = currentModuleSubscription
    ? isSubscriptionExpired(currentModuleSubscription)
    : false;
  /** While sub is active, not expired, and outside renewal window — user cannot switch monthly/yearly (matches paid term until renewal). */
  const lockBillingCycleToCurrentSubscription =
    Boolean(currentModuleSubscription) && !subscriptionExpired && !withinRenewalPeriod;

  useEffect(() => {
    if (!lockBillingCycleToCurrentSubscription || !currentModuleSubscription) return;
    const subType =
      (currentModuleSubscription.subscription_type || 'monthly').toLowerCase() === 'yearly'
        ? 'yearly'
        : 'monthly';
    setBillingCycle(subType);
    onBillingCycleChange?.(subType);
  }, [
    lockBillingCycleToCurrentSubscription,
    currentModuleSubscription?.id,
    currentModuleSubscription?.subscription_type,
    activeModuleTab,
    onBillingCycleChange,
  ]);

  const currentPlan = currentModuleSubscription?.SubscriptionPlan
    ? subscriptionPlans.find(p => p.id === currentModuleSubscription.SubscriptionPlan?.id)
    : null;

  // Plan tier based on price (higher price = higher tier)
  const getPlanPrice = (plan) => {
    const price = billingCycle === 'yearly' ? (plan.yearly_price ?? plan.YearlyPrice) : (plan.monthly_price ?? plan.MonthlyPrice);
    return parseFloat(price) || 0;
  };
  // When there is no current plan for this module, do not compare tiers (avoids every paid plan showing "Upgrade")
  const currentPlanPrice = currentPlan ? getPlanPrice(currentPlan) : null;

  const isPlanLower = (plan) =>
    currentPlanPrice != null && getPlanPrice(plan) < currentPlanPrice;
  const isPlanHigher = (plan) =>
    currentPlanPrice != null && getPlanPrice(plan) > currentPlanPrice;

  // Check if a plan is the current plan based on active subscriptions
  const isPlanCurrent = (plan) => {
    // First check the prop activePlanId (for backward compatibility)
    if (activePlanId && plan.id === activePlanId) {
      return true;
    }
    
    // Check if plan ID matches any active subscription plan ID for the current module
    if (filteredSubscriptions && filteredSubscriptions.length > 0) {
      return filteredSubscriptions.some(subscription => {
        const subscriptionPlanId = subscription.SubscriptionPlan?.id;
        return subscriptionPlanId === plan.id;
      });
    }
    
    return false;
  };

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className="modern-subscription-container">
        <div className="loading-container">
          <div className="spinner-border text-success" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="modern-subscription-container">
        <div className="error-container">
          <i className="fas fa-exclamation-triangle text-danger mb-3"></i>
          <h4>Error Loading Plans</h4>
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-success"
          >
            <i className="fas fa-refresh me-2"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-subscription-container">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Module Tabs */}
      <div className="module-tabs-container mb-4">
        <div className="module-tabs">
          {(moduleTabs.length > 0 ? moduleTabs : [
            { key: 'task', label: 'Tasks' },
            { key: 'document', label: 'Documents' },
          ]).map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`module-tab ${activeModuleTab === tab.key ? 'active' : ''}`}
              onClick={() => handleModuleTabChange(tab.key)}
            >
              <i className={`${getModuleTabIcon(tab.key)} me-2`}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Module Description */}
      <div className="module-description-block">
        <p style={{ fontSize: '1rem', lineHeight: '1.6' }}>
          {getModuleDescriptionForTab(activeModuleTab, { modulesList, subscriptionPlans })}
        </p>
      </div>

      {/* Billing Cycle Toggle */}
      <div
        className={`billing-toggle-container billing-toggle-spacing${lockBillingCycleToCurrentSubscription ? ' billing-toggle-locked' : ''}`}
        title={
          lockBillingCycleToCurrentSubscription
            ? 'Billing cycle matches your current plan until the renewal period.'
            : undefined
        }
      >
        <div className="billing-toggle">
          <span className={`toggle-label ${billingCycle === 'monthly' ? 'active' : ''}`}>
            Monthly
          </span>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={billingCycle === 'yearly'}
              disabled={lockBillingCycleToCurrentSubscription}
              onChange={(e) => {
                if (lockBillingCycleToCurrentSubscription) return;
                handleBillingCycleChange(e.target.checked ? 'yearly' : 'monthly');
              }}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className={`toggle-label ${billingCycle === 'yearly' ? 'active' : ''}`}>
            Yearly
          </span>
        </div>
      </div>

      {/* Subscription Plans */}
      <div className="plans-section mb-4">
        {filteredPlans.length === 0 ? (
          <div className="text-center py-5">
            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
            <p className="text-muted">No plans available for this module.</p>
          </div>
        ) : (
          <div className="plans-grid">
            {filteredPlans.map((plan) => {
            const discountedPrice = calculateDiscountedPrice(plan);
            const originalPrice = billingCycle === 'monthly' ? plan.MonthlyPrice : plan.YearlyPrice;
            const isCurrentPlan = isPlanCurrent(plan);
            const isUpgrade = isPlanHigher(plan);
            const isDowngrade = isPlanLower(plan);

            /** Allow switching to a lesser plan during renewal window or when subscription already expired */
            const renewalOrExpiredAllowsTierChange = withinRenewalPeriod || subscriptionExpired;
            const hideDowngradeButton = isDowngrade && !renewalOrExpiredAllowsTierChange;

            // Button label: Current Plan / Renew (when in renewal period) / Upgrade / Downgrade
            let buttonLabel = 'Select Plan';
            let buttonIcon = 'fa-arrow-right';
            let buttonClass = 'select-plan-btn';
            let isDisabled = false;

            if (isCurrentPlan) {
              if (withinRenewalPeriod) {
                buttonLabel = 'Renew';
                buttonIcon = 'fa-sync-alt';
                buttonClass = 'renew-plan-btn';
                isDisabled = false;
              } else {
                buttonLabel = 'Current Plan';
                buttonIcon = 'fa-check';
                buttonClass = 'current-plan-btn';
                isDisabled = true;
              }
            } else if (isUpgrade) {
              buttonLabel = 'Upgrade';
              buttonIcon = 'fa-arrow-up';
            } else if (isDowngrade) {
              buttonLabel = 'Downgrade';
              buttonIcon = 'fa-arrow-down';
            }

            const moduleKey = plan.module_id != null ? String(plan.module_id) : null;
            const pickedForModule =
              allowMultiModuleCart && selectedPlansByModuleId && moduleKey
                ? selectedPlansByModuleId[moduleKey]
                : null;
            const isParentPicked =
              allowMultiModuleCart && pickedForModule
                ? pickedForModule.id === plan.id
                : parentSelectedPlan && parentSelectedPlan.id === plan.id;

            return (
              <div
                key={plan.id}
                className={`plan-card ${selectedPlanId === plan.id || isParentPicked ? 'selected' : ''} ${isCurrentPlan ? 'active-plan' : ''} ${isParentPicked ? 'parent-selected' : ''}`}
              >
                {/* Active Plan Badge - show "Current Package" until renewal period, then badge can stay */}
                {isCurrentPlan && (
                  <div className="active-badge">
                    <i className="fas fa-check-circle me-1"></i>
                    {withinRenewalPeriod ? 'Renewal Due' : 'Current Plan'}
                  </div>
                )}

                {/* Plan Header with Background Color */}
                <div className="plan-header">
                  <div className="plan-title">{plan.PlanName}</div>
                </div>

                <div className="plan-content">
                  {/* Price and Related Data */}
                  <div className="plan-pricing-section text-center">
                    <div className="plan-pricing">
                      <span className="price-amount">
                        {`$${Number(discountedPrice ?? 0).toFixed(2)}`}
                      </span>
                      <span className="price-period">/{billingCycle === 'monthly' ? 'month' : 'year'}</span>
                    </div>
                    {planIncludesCredits(plan) && (
                      <div className="user-limit-info">
                        <p>
                          {billingCycle === 'monthly'
                            ? (plan.monthly_included_credits != null
                              ? `$${Number(plan.monthly_included_credits).toFixed(2)} credits / month`
                              : plan.yearly_included_credits != null
                                ? `$${Number(plan.yearly_included_credits).toFixed(2)} credits / year (yearly billing)`
                                : 'Credits included with plan')
                            : (plan.yearly_included_credits != null
                              ? `$${Number(plan.yearly_included_credits).toFixed(2)} credits / year`
                              : plan.monthly_included_credits != null
                                ? `$${Number(plan.monthly_included_credits).toFixed(2)} credits / month (monthly billing)`
                                : 'Credits included with plan')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {plan.Desscription && (
                    <div className="plan-description-section">
                      <p className="plan-description">{plan.Desscription}</p>
                    </div>
                  )}

                  {/* Features List */}
                  {(() => {
                    // Helper function to safely parse JSON or return the value
                    const safeParse = (value) => {
                      if (!value) return [];
                      if (Array.isArray(value)) return value;
                      if (typeof value === 'string') {
                        // Check if it's a valid JSON string
                        if (value.trim().startsWith('[') || value.trim().startsWith('{')) {
                          try {
                            return JSON.parse(value);
                          } catch (e) {
                            // If parsing fails, treat as plain string and wrap in array
                            return [value];
                          }
                        } else {
                          // Plain string, split by newline so each line gets a tick icon
                          return value.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
                        }
                      }
                      return [];
                    };

                    // Use whats_included if available, otherwise fallback to features
                    const included = plan.whats_included && plan.whats_included.length > 0
                      ? safeParse(plan.whats_included)
                      : (plan.features && plan.features.length > 0 
                          ? safeParse(plan.features)
                          : []);
                    
                    return included.length > 0 && (
                      <div className="plan-features">
                        <h6 className="features-title text-center">
                          What's Included:
                        </h6>
                        <ul className="features-list">
                          {included.map((item, idx) => {
                            const text = typeof item === 'object' ? (item.display_name || item.name || JSON.stringify(item)) : item;
                            return (
                              <li key={idx} className="feature-item">
                                <i className="fas fa-check text-danger me-2"></i>
                                <span className="feature-item-text" style={{ whiteSpace: 'pre-line' }}>
                                  {text}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })()}

                </div>
                {/* Action Button - Upgrade / Downgrade / Renew / Current Plan (hide Downgrade outside renewal / expiry) */}
                <div className="plan-action">
                  {!hideDowngradeButton && (
                    <button
                      className={buttonClass}
                      onClick={() => {
                        if (isDisabled) return;
                        if (isCurrentPlan && withinRenewalPeriod) {
                          const sub = filteredSubscriptions?.find(s => s.SubscriptionPlan?.id === plan.id);
                          navigate(`/subscription/renew?subscriptionId=${sub?.id || ''}`);
                        } else {
                          handleSelectPlan(plan);
                        }
                      }}
                      disabled={isDisabled}
                    >
                      <i className={`fas ${buttonIcon} me-2`}></i>
                      {buttonLabel}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Current Subscriptions Summary - Filtered by Module */}
      {!subscriptionsLoading && filteredSubscriptions.length > 0 && (
        <div className="subscription-summary-section mt-5">
          <div className="subscription-summary-grid">
            {filteredSubscriptions.map((subscription) => {
              const plan = subscription.SubscriptionPlan;
              const module = plan?.Module;
              const moduleName = (module?.name || module?.display_name || '').toLowerCase();
              const isDocumentModule = moduleName === 'document';
              const isTaskModule = moduleName === 'task';
              const isForumModule =
                moduleName === 'discussion_forum' ||
                moduleName.includes('forum') ||
                moduleName.includes('discussion');
              const totalUsers = subscription.user_count || 0;
              const maxUsers = plan?.is_unlimited_users ? 'Unlimited' : (plan?.max_users || 'N/A');
              
              return (
                <div key={subscription.id} className="subscription-summary-card">
                  <div className="summary-card-body">
                    <div className="summary-header-row">
                      <h5 className="summary-plan-name">Active Subscription Summary</h5>
                      {(() => {
                        const endDate = new Date(subscription.end_date);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        endDate.setHours(0, 0, 0, 0);
                        const isExpired = endDate < today;
                        
                        if (isExpired) {
                          return (
                            <div className="summary-status-badge expired">
                              <i className="fas fa-exclamation-circle me-1"></i>
                              Expired
                            </div>
                          );
                        }
                        return (
                          <div className="summary-status-badge active">
                            <i className="fas fa-check-circle me-1"></i>
                            Active
                          </div>
                        );
                      })()}
                    </div>
                    
                    <div className="summary-details">
                      <div className="summary-detail-item">
                        <i className="fas fa-tag text-primary me-2"></i>
                        <span className="detail-label">Plan Name:</span>
                        <span className="detail-value">{plan?.display_name || plan?.name || 'Unknown Plan'}</span>
                      </div>
                      {/* Show user count only for Task module */}
                      {isTaskModule && (
                        <div className="summary-detail-item">
                          <i className="fas fa-users text-primary me-2"></i>
                          <span className="detail-label">Current Users:</span>
                          <span className="detail-value">{totalUsers} / {maxUsers}</span>
                        </div>
                      )}

                      {isTaskModule && subscription.task_storage && (
                        <div className="summary-detail-item">
                          <i className="fas fa-paperclip text-primary me-2"></i>
                          <span className="detail-label">Storage Used:</span>
                          <span className="detail-value">
                            {subscription.task_storage.used_formatted || formatBytes(subscription.task_storage_used || 0)}
                            {subscription.task_storage.limit_formatted &&
                              subscription.task_storage.limit_formatted !== 'Unlimited' &&
                              subscription.task_storage.limit_formatted !== '0 Bytes' && (
                              <> / {subscription.task_storage.limit_formatted}</>
                            )}
                            {subscription.task_storage.is_unlimited && <> (Unlimited)</>}
                          </span>
                        </div>
                      )}
                      
                      {/* Show storage details only for Document module */}
                      {isDocumentModule && subscription.disk_space_used !== undefined && (
                        <div className="summary-detail-item">
                          <i className="fas fa-hdd text-primary me-2"></i>
                          <span className="detail-label">Storage Used:</span>
                          <span className="detail-value">
                            {subscription.disk_space?.used_formatted || formatBytes(subscription.disk_space_used)}
                            {subscription.disk_space?.limit_formatted && subscription.disk_space?.limit_formatted !== '0 Bytes' && (
                              <> / {subscription.disk_space.limit_formatted}</>
                            )}
                          </span>
                        </div>
                      )}

                      {isForumModule && subscription.forum_storage && (
                        <div className="summary-detail-item">
                          <i className="fas fa-hdd text-primary me-2"></i>
                          <span className="detail-label">Storage Used:</span>
                          <span className="detail-value">
                            {subscription.forum_storage.used_formatted || formatBytes(subscription.forum_storage_used || 0)}
                            {subscription.forum_storage.limit_formatted &&
                              subscription.forum_storage.limit_formatted !== 'Unlimited' &&
                              subscription.forum_storage.limit_formatted !== '0 Bytes' && (
                              <> / {subscription.forum_storage.limit_formatted}</>
                            )}
                            {subscription.forum_storage.is_unlimited && <> (Unlimited)</>}
                          </span>
                        </div>
                      )}
                      
                      <div className="summary-detail-item">
                        <i className="fas fa-calendar-alt text-primary me-2"></i>
                        <span className="detail-label">Start Date:</span>
                        <span className="detail-value">{formatDate(subscription.start_date)}</span>
                      </div>
                      
                      <div className="summary-detail-item">
                        <i className="fas fa-calendar-check text-primary me-2"></i>
                        <span className="detail-label">End Date:</span>
                        <span className="detail-value">{formatDate(subscription.end_date)}</span>
                      </div>
                      
                      {subscription.days_remaining !== undefined && (
                        <div className="summary-detail-item">
                          <i className="fas fa-clock text-primary me-2"></i>
                          <span className="detail-label">Days Remaining:</span>
                          <span className="detail-value">{subscription.days_remaining} days</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Renew Button - Show when expired OR when within renewal period (per doc Section 5) */}
                    {(() => {
                      const endDate = new Date(subscription.end_date);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      endDate.setHours(0, 0, 0, 0);
                      const isExpired = endDate < today;
                      const isInRenewalWindow = isWithinRenewalPeriod(subscription);
                      const showRenewButton = isExpired || isInRenewalWindow;
                      
                      if (showRenewButton) {
                        return (
                          <div className="summary-renew-action mt-3">
                            <button
                              className="renew-btn"
                              onClick={() => navigate(`/subscription/renew?subscriptionId=${subscription.id}`)}
                            >
                              <i className="fas fa-sync-alt me-2"></i>
                              {isExpired ? 'Renew Subscription' : 'Renew'}
                            </button>
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}



      <style jsx>{`
        .modern-subscription-container {
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
          background: white;
          box-sizing: border-box;
          overflow-x: hidden;
        }

        .loading-container, .error-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 300px;
          text-align: center;
        }

        .subscription-header {
          background: linear-gradient(135deg, #C42126 0%, #20c997 100%);
          color: white;
          padding: 3rem 2rem;
          border-radius: 20px;
          margin-bottom: 2rem;
        }

        .module-tabs-container {
          display: flex;
          justify-content: flex-start;
          margin-bottom: 1rem;
        }

        .module-tabs {
          display: flex;
          gap: 0;
          background: transparent;
          padding: 0;
          border-radius: 0;
          border-bottom: 2px solid #e9ecef;
        }

        .module-tab {
          padding: 0.75rem 2rem;
          border: none;
          background: transparent;
          border-radius: 0;
          font-weight: 600;
          font-size: 1rem;
          color: #6c757d;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          border-bottom: 3px solid transparent;
          margin-bottom: -2px;
          position: relative;
          flex: 1;
          max-width: 240px;
          /* Keep tab titles (e.g. "Snap Bill") on a single line */
          white-space: nowrap;
          line-height: 1.2;
        }

        .module-tab:hover {
          color: #C42126;
          background: transparent;
          border-bottom-color: #C42126;
        }

        .module-tab.active {
          background: transparent;
          color: #C42126;
          box-shadow: none;
          border-bottom-color: #C42126;
        }

        .module-tab i {
          font-size: 0.9rem;
        }

        .module-description-block {
          margin-bottom: 0.5rem;
        }

        .module-description-container {
          max-width: 900px;
          margin: 0 auto 1.5rem;
        }

        .module-description {
          display: flex;
          align-items: flex-start;
          gap: 1.5rem;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 1.5rem 2rem;
          border-radius: 15px;
          border-left: 4px solid #C42126;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
        }

        .module-description-icon {
          flex-shrink: 0;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #C42126;
          color: white;
          border-radius: 12px;
          font-size: 1.5rem;
        }

        .module-description-content {
          flex: 1;
        }

        .module-description-title {
          color: #C42126;
          font-weight: 700;
          font-size: 1.25rem;
          margin-bottom: 0.75rem;
        }

        .module-description-text {
          color: #495057;
          font-size: 1rem;
          line-height: 1.7;
          margin: 0;
        }

        .subscription-summary-section {
          background: white;
          margin-bottom: 2rem;
        }

        .subscription-summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .subscription-summary-card {
          background: #f8f9fa;
          border: 2px solid #e9ecef;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .subscription-summary-card:hover {
          border-color: #C42126;
          box-shadow: 0 4px 15px rgba(196, 33, 38, 0.1);
        }

        .summary-card-header {
          background: linear-gradient(135deg, #C42126 0%, #e63946 100%);
          padding: 1rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .summary-module-badge {
          color: white;
          font-weight: 600;
          font-size: 0.95rem;
        }

        .summary-status-badge {
          padding: 0.35rem 0.85rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
        }

        .summary-status-badge.active {
          background: rgba(40, 167, 69, 0.15);
          color: #28a745;
          border: 1px solid rgba(40, 167, 69, 0.3);
        }

        .summary-status-badge.expired {
          background: rgba(220, 53, 69, 0.15);
          color: #dc3545;
          border: 1px solid rgba(220, 53, 69, 0.3);
        }

        .summary-card-body {
          padding: 1.5rem;
        }

        .summary-header-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .summary-plan-name {
          color: #333;
          font-size: 1.25rem;
          font-weight: 700;
          margin: 0;
        }

        .summary-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.75rem;
        }

        .summary-detail-item {
          display: flex;
          align-items: center;
          font-size: 0.9rem;
        }

        .summary-detail-item i {
          width: 20px;
          text-align: center;
        }

        .detail-label {
          color: #6c757d;
          margin-right: 0.5rem;
          min-width: 120px;
        }

        .detail-value {
          color: #333;
          font-weight: 600;
        }

        /* Scoped under .modern-subscription-container so global .billing-toggle (subscriptionplan.css) cannot override */
        .modern-subscription-container .billing-toggle-container {
          display: flex;
          justify-content: center;
          flex-wrap: nowrap;
          overflow: visible;
        }

        .modern-subscription-container .billing-toggle-container.billing-toggle-spacing {
          margin-top: 0.25rem;
          margin-bottom: 1rem;
        }

        .modern-subscription-container .billing-toggle-container.billing-toggle-locked .billing-toggle {
          opacity: 0.72;
        }

        .modern-subscription-container .billing-toggle-container.billing-toggle-locked .toggle-switch input:disabled + .toggle-slider {
          cursor: not-allowed;
          opacity: 0.85;
        }

        .modern-subscription-container .billing-toggle {
          display: inline-flex;
          flex-direction: row;
          align-items: center;
          flex-wrap: nowrap;
          gap: 1rem;
          background: #f8f9fa;
          padding: 0.75rem 1rem;
          border-radius: 50px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
          text-align: left;
          margin: 0;
          white-space: nowrap;
          min-width: min-content;
        }

        .modern-subscription-container .billing-toggle .toggle-label {
          font-weight: 600;
          color: #6c757d;
          position: relative;
          display: inline-block;
          padding: 0.5rem 1rem;
          border-radius: 25px;
          transition: color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
          flex: 0 0 auto;
          white-space: nowrap;
        }

        .modern-subscription-container .billing-toggle .toggle-label.active {
          color: #C42126;
          background: white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .save-badge {
          background: #ffc107;
          color: #000;
          font-size: 0.7rem;
          padding: 0.2rem 0.5rem;
          border-radius: 10px;
          margin-left: 0.5rem;
        }

        .modern-subscription-container .billing-toggle .toggle-switch {
          position: relative;
          display: inline-block;
          width: 60px;
          height: 30px;
          flex: 0 0 60px;
        }

        .modern-subscription-container .billing-toggle .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .modern-subscription-container .billing-toggle .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.4s;
          border-radius: 30px;
        }

        .modern-subscription-container .billing-toggle .toggle-slider:before {
          position: absolute;
          content: "";
          height: 24px;
          width: 24px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.4s;
          border-radius: 50%;
        }

        .modern-subscription-container .billing-toggle .toggle-switch input:checked + .toggle-slider {
          background-color: #C42126;
        }

        .modern-subscription-container .billing-toggle .toggle-switch input:checked + .toggle-slider:before {
          transform: translateX(30px);
        }

        .promo-card {
          background: linear-gradient(135deg, #fffbf0 0%, #fef7e0 100%);
          border: 2px solid #ffc107;
          border-radius: 15px;
          padding: 1.5rem;
        }

        .promo-header {
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }

        .promo-input-group {
          display: flex;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .promo-input {
          flex: 1;
          padding: 0.75rem 1rem;
          border: 2px solid #e9ecef;
          border-radius: 10px;
          font-size: 1rem;
          transition: border-color 0.3s ease;
        }

        .promo-input:focus {
          outline: none;
          border-color: #C42126;
        }

        .promo-button {
          padding: 0.75rem 1.5rem;
          background: #C42126;
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.3s ease;
        }

        .promo-button:hover {
          background: #C42126;
        }

        .promo-success {
          color: #C42126;
          font-weight: 600;
        }

        .discount-text {
          color: #ffc107;
          font-weight: 700;
        }

        .plans-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 1.5rem;
          width: 100%;
          margin: 0 auto;
        }

        .plan-card {
          background: white;
          border: 2px solid #e9ecef;
          border-radius: 15px;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
        }

        .plan-header {
          text-align: center;
        }

        .plan-header .plan-title {
          color: #333;
          font-size: 1.5rem;
          margin: 0;
          font-weight: 800;
        }

        .plan-content {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .plan-pricing-section {
          padding: 0.5rem 0;
        }

        .user-limit-info {
          margin-top: 0.75rem;
        }

        .user-limit-info p {
          margin: 0;
        }

        .plan-description-section {
          padding: 0.75rem 0;
        }

        .plan-description-section .plan-description {
          color: #6c757d;
          margin: 0;
          font-size: 0.95rem;
          line-height: 1.6;
        }

        .plan-features {
          flex: 1;
          margin-top: auto;
        }

        .features-list {
          text-align: left;
          padding-left: 0;
          margin: 0;
          font-size: 0.9rem;
        }

        .plan-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px rgba(0,0,0,0.1);
        }

        .plan-card.selected {
          border-color: #C42126;
          transform: translateY(-5px);
        }

        .plan-card.active-plan {
          border-color: #C42126;
          position: relative;
        }

        .plan-card.parent-selected {
          border-color: #C42126;
          transform: translateY(-5px);
        }

        .active-badge {
          position: absolute;
          top: -15px;
          left: 50%;
          transform: translateX(-50%);
          background-color: #C42126;
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          z-index: 10;
        }

        .plan-action {
          margin-top: auto;
          padding: 0.75rem 1.5rem 1rem;
        }

        .current-plan-btn {
          width: 100%;
          padding: 0.75rem 1rem;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: not-allowed;
          opacity: 0.8;
        }

        .select-plan-btn {
          width: 100%;
          padding: 0.75rem 1rem;
          background: #C42126;
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .select-plan-btn:hover {
          transform: translateY(-2px);
        }

        .renew-plan-btn {
          width: 100%;
          padding: 0.75rem 1rem;
          background: #C42126;
          color: white;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .renew-plan-btn:hover {
          background: #a01a1f;
          transform: translateY(-2px);
        }

        .summary-renew-action {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e9ecef;
          display: flex;
          justify-content: center;
        }

        .renew-btn {
          width: 250px;
          padding: 0.75rem 1rem;
          background: #C42126;
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.95rem;
        }

        .renew-btn:hover {
          background: #a01a1f;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(196, 33, 38, 0.3);
        }

        .plan-image img {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 15px;
          margin-bottom: 1rem;
        }

        .plan-title {
          font-weight: 800;
          color: #333;
          font-size: 1.5rem;
        }

        .plan-details {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 1rem;
          font-size: 0.9rem;
          color: #6c757d;
        }

        .plan-pricing {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 0.5rem;
        }

        .price-symbol {
          font-size: 1.5rem;
          font-weight: 600;
          color: #C42126;
        }

        .price-amount {
          font-size: 2rem;
          font-weight: 600;
          color: #C42126;
        }

        .price-period {
          font-size: 1rem;
          color: #6c757d;
        }

        .original-price {
          text-decoration: line-through;
          color: #dc3545;
          font-size: 0.9rem;
        }

        .trial-badge {
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          margin-bottom: 1rem;
          display: inline-block;
        }

        .yearly-cost {
          margin: 1rem 0;
          padding: 0.75rem;
          background: #f8f9fa;
          border-radius: 10px;
          text-align: center;
        }

        .yearly-label {
          display: block;
          font-size: 0.8rem;
          color: #6c757d;
          margin-bottom: 0.25rem;
        }

        .yearly-amount {
          font-size: 1.2rem;
          font-weight: 700;
          color: #C42126;
        }

        .plan-features {
          margin-top: auto;
          padding-top: 0.75rem;
        }

        .features-title {
          font-size: 0.95rem;
          color: #495057;
          margin-bottom: 0.75rem;
          font-weight: 600;
        }

        .features-list {
          list-style: none;
          padding: 0;
          margin: 0;
          text-align: left;
        }

        .feature-item {
          font-size: 0.9rem;
          color: #495057;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: flex-start;
          line-height: 1.4;
        }

        .feature-item i {
          margin-top: 0.15rem;
          flex-shrink: 0;
        }

        .feature-more {
          font-size: 0.8rem;
          color: #C42126;
          font-weight: 600;
          margin-top: 0.5rem;
          display: flex;
          align-items: center;
        }


        .submit-button-subs {
          width: 100%;
          padding: 1rem 2rem;
          background: #C42126;
          color: white;
          border: none;
          border-radius: 15px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 1rem;
        }

        .submit-button-subs:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(196, 33, 38, 0.3);
        }

        .submit-button-subs:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 1200px) {
          .plans-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 1.25rem;
          }
        }

        @media (max-width: 768px) {
          .modern-subscription-container {
            padding: 1rem;
          }
          
          .module-tabs {
            flex-wrap: wrap;
          }

          .module-tab {
            padding: 0.5rem 1rem;
            font-size: 0.9rem;
            white-space: nowrap;
          }

          .module-description {
            flex-direction: column;
            padding: 1.25rem 1.5rem;
            gap: 1rem;
          }

          .module-description-icon {
            width: 45px;
            height: 45px;
            font-size: 1.25rem;
          }

          .module-description-title {
            font-size: 1.1rem;
          }

          .module-description-text {
            font-size: 0.95rem;
          }
          
          .plans-grid {
            grid-template-columns: 1fr;
            max-width: 100%;
          }

          .subscription-summary-grid {
            grid-template-columns: 1fr;
          }

          .summary-details {
            grid-template-columns: 1fr;
          }

          .plan-card {
            max-width: 100%;
          }

          .plan-header {
            padding: 1.25rem 1.5rem;
          }

          .plan-header .plan-title {
            font-size: 1.25rem;
          }
          
          .promo-input-group {
            flex-direction: column;
          }
        }

        @media (max-width: 400px) {
          .summary-header-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }

          .summary-plan-name {
            font-size: 1.05rem;
            line-height: 1.35;
          }

          .summary-card-body {
            padding: 1rem;
          }

          .summary-detail-item {
            display: grid;
            grid-template-columns: 1.25rem 1fr;
            column-gap: 0.5rem;
            row-gap: 0.2rem;
            align-items: start;
          }

          .summary-detail-item i {
            grid-column: 1;
            grid-row: 1 / span 2;
            align-self: start;
            margin-right: 0 !important;
          }

          .summary-detail-item .detail-label {
            grid-column: 2;
            grid-row: 1;
            min-width: 0;
            margin-right: 0;
          }

          .summary-detail-item .detail-value {
            grid-column: 2;
            grid-row: 2;
            min-width: 0;
            text-align: left;
            word-break: break-word;
          }
        }
      `}</style>
    </div>
  );
};

export default CorporateSubscription;
